// Libraries
import React, { useState, useEffect, useContext } from 'react';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRectangleList } from '@fortawesome/free-solid-svg-icons';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { Alert, AlertTitle, Box, Button } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { toast } from 'react-toastify';

// Components
import GeneralModal from '../../components/Modals/GeneralModal.js';
import Shot from '../../components/Fragments/Shot.js';
import ProFlag from '../../components/Elements/ProFlag.js';
import ShotsToolbar from '../../components/Fragments/ProjectHeader/ShotsToolbar.js';
import headerTheme from '../../components/Fragments/ProjectHeader/headerTheme.js';
import UpgradeMembership from '../../components/Modals/Subscription/upgradeMembership.js';

// Context
import { UserContext } from '../../context/User';
import ScriptContext from '../../context/Script';
import SessionContext from '../../context/Session';
import ProjectViewContext from '../../context/ProjectView';

// Services
import dictionaryService from '../../services/dictionaryService';
import shotService from '../../services/shotService';
import scriptService from '../../services/scriptService';
import userService from '../../services/userService';
import useScenePermissions from '../../hooks/useScenePermissions';
import useExpandAllShots from '../../hooks/useExpandAllShots';
import analyticsService from '../../services/analyticsService';

// Reconciles a desired shot ordering/structure (which may have been computed from a
// stale snapshot) against the latest known shot_list, so that field edits committed
// concurrently (e.g. a description saved on blur) aren't discarded by a structural
// change (add/delete/reorder) that resolves around the same time.
function reconcileShotList(latestShotList, desiredShotList) {
  const latestById = new Map(latestShotList.map((s) => [s.id, s]));
  return desiredShotList.map((desired, i) => ({
    ...(latestById.get(desired.id) || desired),
    shot_number: i + 1,
  }));
}

// Construct Component
const ShotTable = () => {
  const { user, setUser } = useContext(UserContext);
  const { script, setScript, updateScriptScene } = useContext(ScriptContext);
  const { isMobile, generalModalDetails, setGeneralModalDetails } = useContext(SessionContext);
  const { view, setView } = useContext(ProjectViewContext);
  const [selectedShot, setSelectedShot] = useState(null);
  const [loadComplete, setLoadComplete] = useState(false);
  const [shotDictionaries, setShotDictionaries] = useState({
    frame: [],
    focus: [],
    camera: [],
    movement: [],
    focal: [],
    subjects: [],
    types: [],
    special_equipment: [],
  });

  const currentScene = script.scenes?.[view.sceneIndex];
  const { canEditScene, isSceneLocked, isGenerationLocked } = useScenePermissions(
    currentScene,
    script,
  );
  const isDraggable = canEditScene;
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);

  // One-time nudge that the AI output is a draft to edit, not a final answer.
  // Shown on the first AI-generated shotlist (ai_status 2) the user can edit,
  // until they dismiss it.
  const showSuggestedBanner =
    !!user?.id &&
    !user.suggestedShotlistBannerDismissed &&
    canEditScene &&
    currentScene?.ai_status === 2 &&
    currentScene?.shot_list?.length > 0;

  const dismissSuggestedBanner = () => {
    analyticsService.trackEvent('suggested_shotlist_banner_dismissed');
    userService.dismissSuggestedShotlistBanner();
    // Clear it locally too, so the cached user doesn't bring it back.
    setUser({ ...user, suggestedShotlistBannerDismissed: true });
  };

  const addShot = async (shotId, sceneIndex = view.sceneIndex) => {
    try {
      const currentScene = script.scenes[sceneIndex];
      const shotNumber = shotId
        ? currentScene.shot_list.find((s) => s.id === shotId).shot_number + 1
        : 1;
      const newShot = await shotService.addShot(currentScene.id, shotNumber);

      const desiredShotList = [...currentScene.shot_list];
      if (shotId) {
        const shotIndex = desiredShotList.findIndex((s) => s.id === shotId) + 1;
        desiredShotList.splice(shotIndex, 0, newShot);
      } else {
        desiredShotList.push(newShot);
      }

      setSelectedShot(newShot.id);
      await updateShotNumbers({ ...currentScene, shot_list: desiredShotList });
    } catch (error) {
      console.error('Error adding shot:', error);
    }
  };

  const handleShotFieldChange = async (sceneNumber, shotId, field, update) => {
    try {
      // shotId 0 is Utils.buildTempShot's placeholder row (shown for a scene with no
      // real shots yet) -- it was never persisted, so there's nothing to save server-side.
      // Saving would 400 (the API now requires a real shot id); just update local state.
      if (shotId !== 0) {
        await scriptService.changeShotChange(shotId, field, update, sceneNumber);
      }

      // Built from the current render's script rather than captured inside the setScript
      // updater below: React may run that updater lazily, after this function has moved
      // on, which left deletedScene null and skipped renumbering. updateShotNumbers
      // reconciles field values against the latest state, so a slightly stale snapshot
      // here only supplies the order.
      let deletedScene = null;
      if (field === 'deleted_at') {
        const scene = script.scenes.find((s) => s.id === sceneNumber);
        if (scene) {
          deletedScene = { ...scene, shot_list: scene.shot_list.filter((s) => s.id !== shotId) };
        }
      }

      setScript((prevScript) => {
        const sceneIndex = prevScript.scenes.findIndex((scene) => scene.id === sceneNumber);
        if (sceneIndex === -1) return prevScript;

        const prevScene = prevScript.scenes[sceneIndex];
        const shotIndex = prevScene.shot_list.findIndex((s) => s.id === shotId);
        if (shotIndex === -1) return prevScript;

        const updatedShotList = [...prevScene.shot_list];
        if (field === 'deleted_at') {
          updatedShotList.splice(shotIndex, 1);
        } else {
          updatedShotList[shotIndex] = { ...updatedShotList[shotIndex], [field]: update };
        }

        const updatedScene = { ...prevScene, shot_list: updatedShotList };
        if (field === 'deleted_at' && updatedShotList.length === 0) {
          updatedScene.generation_error = null;
        }

        const updatedScenes = [...prevScript.scenes];
        updatedScenes[sceneIndex] = updatedScene;
        return { ...prevScript, scenes: updatedScenes };
      });

      if (field === 'deleted_at' && deletedScene) {
        await updateShotNumbers(deletedScene);
      }
    } catch (error) {
      console.error('Error handling shot field change:', error);
      // Rethrow so callers (e.g. confirmedDeleteHandler) know the save failed instead
      // of reporting success while the local state update above never ran.
      throw error;
    }
  };

  // `scene.shot_list` reflects the desired ORDER/STRUCTURE (an insert, delete, or
  // reorder) but may have been computed from a snapshot that's since gone stale for
  // individual shots' field values (e.g. a description saved concurrently on blur).
  // The final merge below re-reads field values from the latest state per-shot so a
  // structural change here can never stomp an unrelated field edit that lands around
  // the same time.
  const updateShotNumbers = async (scene) => {
    const shifted = [];
    scene.shot_list.forEach((shot, i) => {
      const shotNum = i + 1;
      if (shot.shot_number !== shotNum) {
        shifted.push({ shotId: shot.id, shotNumber: shotNum });
      }
    });

    if (shifted.length > 0) {
      await scriptService.renumberShots(scene.id, shifted);
    }

    setScript((prevScript) => {
      const sceneIndex = prevScript.scenes.findIndex((s) => s.id === scene.id);
      if (sceneIndex === -1) return prevScript;

      const prevScene = prevScript.scenes[sceneIndex];
      const reconciled = reconcileShotList(prevScene.shot_list, scene.shot_list);

      const updatedScenes = [...prevScript.scenes];
      updatedScenes[sceneIndex] = { ...prevScene, shot_list: reconciled };
      return { ...prevScript, scenes: updatedScenes };
    });
  };

  const updateShot = async (
    sceneId,
    updatedShot,
    updatedField = 'full',
    previousId = updatedShot.id,
  ) => {
    try {
      let success = true;
      setScript((prevScript) => {
        const sceneIndex = prevScript.scenes.findIndex((scene) => scene.id === sceneId);
        if (sceneIndex === -1) {
          success = false;
          return prevScript;
        }

        const prevScene = prevScript.scenes[sceneIndex];
        const shotIndex = prevScene.shot_list.findIndex((s) => s.id === previousId);
        if (shotIndex === -1) {
          success = false;
          return prevScript;
        }

        const updatedShotList = [...prevScene.shot_list];
        updatedShotList[shotIndex] =
          updatedField === 'full'
            ? updatedShot
            : { ...updatedShotList[shotIndex], [updatedField]: updatedShot[updatedField] };

        const updatedScenes = [...prevScript.scenes];
        updatedScenes[sceneIndex] = { ...prevScene, shot_list: updatedShotList };
        return { ...prevScript, scenes: updatedScenes };
      });

      return success;
    } catch (error) {
      console.error('Error updating shot:', error);
      return false;
    }
  };

  const confirmedDeleteHandler = async (sceneNumber, id) => {
    try {
      await handleShotFieldChange(
        sceneNumber,
        id,
        'deleted_at',
        moment().format('YYYY-MM-DD HH:mm:ss'),
      );
      toast.success('Your shot has been deleted.');
    } catch (error) {
      toast.error('We were unable to delete this shot. Please try again.');
    }
  };

  const expandAll = useExpandAllShots();

  const expandContractShot = (shot) => {
    shot.expanded = !shot.expanded;
    updateShot(script.scenes[view.sceneIndex].id, shot, 'expanded');
  };

  const handleDragEnd = async (result) => {
    if (!result.destination || !isDraggable) return;
    const { source, destination } = result;
    if (source.index === destination.index) return;

    setSelectedShot(script.scenes[view.sceneIndex].shot_list[source.index].id);
    const updatedShots = Array.from(script.scenes[view.sceneIndex].shot_list);
    const [movedShot] = updatedShots.splice(source.index, 1);
    updatedShots.splice(destination.index, 0, movedShot);

    script.scenes[view.sceneIndex].shot_list = updatedShots;
    updateScriptScene(script.scenes);
    updateShotNumbers(script.scenes[view.sceneIndex]);
  };

  useEffect(() => {
    const setupDictionaries = async () => {
      if (shotDictionaries['frame'].length === 0) {
        let frames = await dictionaryService.fetchUserDictionary('frame', script.user_id);
        shotDictionaries['frame'] = frames;
      }
      if (shotDictionaries['focus'].length === 0) {
        let focus = await dictionaryService.fetchUserDictionary('focus', script.user_id);
        shotDictionaries['focus'] = focus;
      }
      if (shotDictionaries['camera'].length === 0) {
        let camera = await dictionaryService.fetchUserDictionary('camera', script.user_id);
        shotDictionaries['camera'] = camera;
      }
      if (shotDictionaries['movement'].length === 0) {
        let movement = await dictionaryService.fetchUserDictionary('movement', script.user_id);
        shotDictionaries['movement'] = movement;
      }
      if (shotDictionaries['focal'].length === 0) {
        let focal = await dictionaryService.fetchUserDictionary('focal', script.user_id);
        shotDictionaries['focal'] = focal;
      }

      let subjects = await dictionaryService.fetchScriptDictionary('subjects', script.id);
      shotDictionaries['subjects'] = subjects;

      const allShots = await dictionaryService.fetchUserDictionary('shottype', user.id);
      shotDictionaries['types'] = allShots.map((t) => t.label);

      const specialEquipment = await dictionaryService.fetchScriptDictionary(
        'special_equipment',
        script.id,
        'equipment',
      );
      shotDictionaries['special_equipment'] = specialEquipment.map((e) => e.label);

      setShotDictionaries({ ...shotDictionaries });
      setLoadComplete(true);
    };

    setupDictionaries();
  }, [view.sceneIndex, view.updateDictionary]);

  // Only re-apply the global expand state on scene change; dictionary refreshes
  // (e.g. adding new special equipment) must not collapse individually expanded shots.
  useEffect(() => {
    expandAll(view.expandShots);
  }, [view.sceneIndex]);

  useEffect(() => {
    const updateDictionaries = async () => {
      try {
        // Exit if no updates are needed
        if (!Object.values(view.updateDictionary).some((value) => value === true)) return;

        const updatedDictionaries = {};
        const updatedFlags = {};

        for (const key of Object.keys(view.updateDictionary)) {
          if (view.updateDictionary[key] === true) {
            let dictionary = [];
            if (key === 'subjects') {
              dictionary = await dictionaryService.fetchScriptDictionary(key, script.id);
            } else if (key === 'special_equipment') {
              const equipment = await dictionaryService.fetchScriptDictionary(
                key,
                script.id,
                'equipment',
              );
              dictionary = equipment.map((e) => e.label);
            } else if (key === 'types') {
              const shots = await dictionaryService.fetchUserDictionary('shottype', user.id);
              dictionary = shots.map((t) => t.label);
            } else {
              dictionary = await dictionaryService.fetchUserDictionary(key, script.user_id);
            }
            updatedDictionaries[key] = dictionary;
            updatedFlags[key] = false;
          }
        }

        // Update shot dictionaries with the fetched values
        setShotDictionaries((prevDictionaries) => ({
          ...prevDictionaries,
          ...updatedDictionaries,
        }));

        // Update view to set all flags in updateDictionary to false
        setView((prevView) => ({
          ...prevView,
          updateDictionary: {
            ...prevView.updateDictionary,
            ...updatedFlags,
          },
        }));
      } catch (error) {
        console.error('Error updating dictionaries:', error);
      }
    };

    updateDictionaries();
  }, [view.updateDictionary, script.id]);

  return (
    <div className={`shotTable`} style={{ marginLeft: '20px' }}>
      {generalModalDetails && (
        <GeneralModal
          generalModalDetails={generalModalDetails}
          setModalIsOpen={setGeneralModalDetails}
        />
      )}

      <UpgradeMembership
        isOpen={upgradeModalOpen}
        onRequestClose={() => setUpgradeModalOpen(false)}
        featureMsg="Continue shotlisting fast with Pro features"
      />

      {script.scenes[view.sceneIndex] &&
      script.scenes[view.sceneIndex].shot_list &&
      script.scenes[view.sceneIndex].shot_list.length > 0 ? (
        <DragDropContext onDragEnd={isDraggable ? handleDragEnd : () => {}}>
          <div className="fullWidthContainer">
            {isMobile ? (
              <h3 className="OnePortionFlex">
                {script.scenes[view.sceneIndex].scene_number ? (
                  <>{script.scenes[view.sceneIndex].scene_number}.</>
                ) : null}
                {script.scenes[view.sceneIndex].scene_int_ext}{' '}
                {script.scenes[view.sceneIndex].header_text}{' '}
                {script.scenes[view.sceneIndex].scene_time_of_day}
              </h3>
            ) : null}

            {/* Above phone width this toolbar lives in ProjectHeader's second row. */}
            {isMobile && (
              <ThemeProvider theme={headerTheme}>
                <Box sx={{ py: 1, bgcolor: '#FAFAFA' }}>
                  <ShotsToolbar showExport={false} />
                </Box>
              </ThemeProvider>
            )}

            {isSceneLocked && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                  bgcolor: 'rgba(122,100,235,0.08)',
                  borderRadius: 2,
                  px: 2.5,
                  py: 1.5,
                  mt: 1.5,
                  mb: 1,
                  flexWrap: 'wrap',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: '50%',
                      bgcolor: '#7A64EB',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <AutoAwesomeIcon sx={{ color: '#fff', fontSize: 18 }} />
                  </Box>
                  <Box>
                    <Box sx={{ fontWeight: 700, color: '#1f1f1f', fontSize: 15 }}>
                      Upgrade to edit this scene
                    </Box>
                    <Box sx={{ color: '#6b6b6b', fontSize: 13 }}>
                      View-only on free plan · Editing, image generation, notes, and export
                      require Pro
                    </Box>
                  </Box>
                </Box>
                <Button
                  variant="contained"
                  onClick={() => {
                    analyticsService.trackEvent('locked_scene_edit_attempted');
                    analyticsService.trackEvent('upgrade_cta_clicked', { source: 'shot_panel' });
                    setUpgradeModalOpen(true);
                  }}
                  sx={{
                    bgcolor: '#7A64EB',
                    textTransform: 'none',
                    fontWeight: 700,
                    borderRadius: 2,
                    px: 3,
                    whiteSpace: 'nowrap',
                    '&:hover': { bgcolor: '#6a55e6' },
                  }}
                >
                  Upgrade to Pro →
                </Button>
              </Box>
            )}

            {showSuggestedBanner && (
              <Alert
                icon={<AutoAwesomeIcon sx={{ color: '#7A64EB' }} />}
                onClose={dismissSuggestedBanner}
                sx={{
                  bgcolor: '#EDE7FF',
                  color: '#1f1f1f',
                  borderRadius: 2,
                  mt: 1.5,
                  mb: 1,
                  '& .MuiAlert-action': { color: '#6b6b6b' },
                }}
              >
                <AlertTitle sx={{ fontWeight: 700 }}>
                  Your suggested shotlist is ready. Make it yours.
                </AlertTitle>
                This is a suggested shotlist, a starting point. Edit, reorder, delete or add shots
                to match your vision.
              </Alert>
            )}

            {script.scenes[view.sceneIndex] && script.scenes[view.sceneIndex].generation_error && (
              <div
                className="fullWidthContainer"
                style={{ paddingLeft: '20px', fontStyle: 'italic' }}
              >
                {script.scenes[view.sceneIndex].generation_error.startsWith('Oops')
                  ? script.scenes[view.sceneIndex].generation_error
                  : <>AI could not generate scenes due to{' '}{script.scenes[view.sceneIndex].generation_error}</>
                }
              </div>
            )}
          </div>

          <div
            className={`shotList hiddenScroll ${
              isMobile ? '' : view.viewColumns === 2 ? 'limitHeight' : 'limitHeightSmall'
            }`}
          >
            <Droppable droppableId="shot-list" direction="vertical">
              {(provided) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  style={{ marginBottom: '80px' }}
                >
                  {script.scenes[view.sceneIndex].shot_list &&
                    script.scenes[view.sceneIndex].shot_list.length > 0 &&
                    loadComplete &&
                    script.scenes[view.sceneIndex].shot_list.map((shot, index) => (
                      // Keyed by shot id, not index: Shot holds local state (e.g. the
                      // fade-out isExiting flag), and index keys would hand that state to
                      // whichever shot slides into a deleted shot's slot.
                      <div key={shot.id}>
                        <Draggable
                          key={shot.id}
                          draggableId={String(shot.id)}
                          index={index}
                          isDragDisabled={!isDraggable}
                        >
                          {(provided) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              onClick={() => setSelectedShot(shot.id)}
                            >
                              <Shot
                                shot={shot}
                                isSelected={shot.id === selectedShot}
                                updateShot={updateShot}
                                scene={script.scenes[view.sceneIndex]}
                                dictionaries={shotDictionaries}
                                updateDictionaries={setShotDictionaries}
                                onConfirmDelete={confirmedDeleteHandler}
                                addShot={addShot}
                                view={view}
                              />
                            </div>
                          )}
                        </Draggable>
                      </div>
                    ))}
                </div>
              )}
            </Droppable>
          </div>
        </DragDropContext>
      ) : !isGenerationLocked ? (
        <div className="shotList sizeUp">
          <div className="fullWidthContainer noShotlistIconBox">
            <span className="OnePortionFlex">&nbsp;</span>
            <span className="OnePortionFlex">
              <p>
                <FontAwesomeIcon
                  icon={faRectangleList}
                  className="dkGrayText circleIcon noShotlistIcon"
                />
              </p>
            </span>
            <span className="OnePortionFlex">&nbsp;</span>
          </div>
          <div className="fullWidthContainer">
            {script.scenes[view.sceneIndex] &&
            (script.scenes[view.sceneIndex].ai_status === 1 ||
              script.scenes[view.sceneIndex].isGeneratingShots) ? (
              <h3 className="centered">Please wait while your suggested shotlist is generating</h3>
            ) : view.uploadedFile &&
              script.scenes[view.sceneIndex] &&
              !script.scenes[view.sceneIndex].scene_text ? (
              <span className="centered">
                <h3>Shotlist not available</h3>
                Scene does not contain script text.
              </span>
            ) : script.scenes[view.sceneIndex] && script.scenes[view.sceneIndex].scene_text ? (
              <span className="centered">
                <h3>No shotlist yet</h3>
                Click generate to get started
              </span>
            ) : (
              <span className="centered">
                <h3>No shotlist yet</h3>
                Add scene text to the left and click generate to get started
              </span>
            )}
          </div>
          <div className="fullWidthContainer">
            <p className="centered"></p>
          </div>
        </div>
      ) : null}

      {isGenerationLocked &&
      script.scenes[view.sceneIndex] &&
      script.scenes[view.sceneIndex].ai_status < 1 ? (
        <div
          className="shotList"
          style={{ marginTop: '10px', marginBottom: '60px', textAlign: 'center' }}
        >
          <div style={{ paddingRight: '0px' }}>
            <ProFlag
              type="pro"
              scriptLevel={script ? script.features : 'Basic'}
              scriptId={script.id}
              handleUpgrade={() =>
                setScript((prev) => ({
                  ...prev,
                  features: 'Pro',
                }))
              }
            />
            <span className="label sizeUp" style={{ paddingLeft: '10px' }}>
              You are out of Pro credits <br />
              <br />
            </span>
          </div>
          <div>
            Continue shotlisting manually, <br />
            or upgrade now. <br />
            <br />
            <br />
            <br />
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default ShotTable;
